// common/FiltersFormEngine/querySelectors.ts

import { DashboardFilterInstance, FilterQuery, FiltersRegistry } from "../../context/FiltersProvider/types";
import { FilterCombinationPayload, FilterPayload } from "../../datasources/api-client/data-contracts";
import { NO_VALUE_OPERATORS } from "./operatorRules";
import { FilterItem, RowFilter } from "./types";

// Traversal utilities for advanced filters tree structure

// Computes the maximum depth of the filter tree.
// Used to limit how deeply users can nest groups.
export const getMaxDepth = (items: FilterItem[], depth = 1): number => {
	let max = depth;

	for (const item of items) {
		if (item.type === "group") {
			const childDepth = getMaxDepth(item.value.rows, depth + 1);
			max = Math.max(max, childDepth);
		}
	}

	return max;
};

// Checks whether the user can still add a new group
// based on the maximum allowed nesting depth (3 levels).
export const canAddGroup = (items: FilterItem[]): boolean => {
	return getMaxDepth(items) < 3;
};

// Flattens the entire tree and extracts only row nodes
// This is useful when validating or evaluating all leaf filters.
export const getAllRows = (items: FilterItem[]) => {
	const rows: any[] = [];

	const walk = (nodes: FilterItem[]) => {
		for (const item of nodes) {
			if (item.type === "row") {
				rows.push(item);
			} else {
				walk(item.value.rows);
			}
		}
	};

	walk(items);

	return rows;
};

// Validates advanced filter form state.
// A valid state requires:
// - at least one row
// - each row must have filter + operator
// - value must be present unless operator does not require one
export const isAdvancedFiltersValid = (items: FilterItem[]): boolean => {
	const rows = getAllRows(items);

	if (!rows.length) return false;

	return rows.every((rowItem) => {
		const row = rowItem.value;

		const hasFilter = row.filter != null && row.filter !== "";

		const hasOperator = row.operator != null && row.operator !== "";

		const noValueNeeded = NO_VALUE_OPERATORS.includes(row.operator as any);

		const hasValue = noValueNeeded
			? true
			: Array.isArray(row.value)
				? row.value.length > 0
				: row.value != null && row.value !== "";

		return hasFilter && hasOperator && hasValue;
	});
};

// Validates simple dashboard filters (chip-based filters).
// Ensures at least one active filter and that each filter is complete.
export const isSimpleFiltersValid = (filters: DashboardFilterInstance[]): boolean => {
	const activeFilters = filters.filter((f) => f.filterId);

	if (!activeFilters.length) return false;

	return activeFilters.every((filter) => {
		const hasFilter = filter.filterId != null && filter.filterId !== "";

		const hasOperator = filter.operator != null;

		const noValueNeeded = NO_VALUE_OPERATORS.includes(filter.operator as any);

		const hasValue = noValueNeeded
			? true
			: Array.isArray(filter.value)
				? filter.value.length > 0
				: filter.value != null && filter.value !== "";

		return hasFilter && hasOperator && hasValue;
	});
};

// Checks whether a filter name is available inside the registry.
// Used to prevent duplicate saved filter names.
type Params = {
	registry: FiltersRegistry | null;
	name: string;
	excludeId?: string;
};

export const isFilterNameAvailable = ({ registry, name }: Params): boolean => {
	if (!registry) return true;

	const normalizedName = name.trim().toLowerCase();

	if (!normalizedName.length) return false;

	return !registry.items.some((filter) => {
		return filter.name.trim().toLowerCase() === normalizedName;
	});
};

// Utility: type guard used to distinguish payload nodes
const isFilterPayload = (node: FilterPayload | FilterCombinationPayload): node is FilterPayload => {
	return "id" in node;
};

// Flattens a nested filter query into a single list of filter rules.
// Group nodes are recursively traversed.
export const flattenQueryFilters = (query?: FilterQuery | null): FilterPayload[] => {
	if (!query?.filters?.length) {
		return [];
	}

	const result: FilterPayload[] = [];

	const walk = (nodes: (FilterPayload | FilterCombinationPayload)[]) => {
		for (const node of nodes) {
			if (isFilterPayload(node)) {
				result.push(node);
				continue;
			}

			if (node.filters?.length) {
				walk(node.filters);
			}
		}
	};

	walk(query.filters);

	return result;
};

// Returns how many filters are currently applied in a payload.
// Useful for badges, counters, and UI indicators.
export const getAppliedFiltersCount = (payload?: FilterCombinationPayload | null): number => {
	if (!payload?.filters?.length) {
		return 0;
	}

	return flattenQueryFilters(payload).length;
};

// Checks if a dashboard filter is already applied in the current payload.
// Compares filter id, operator, and value.
export const isDashboardFilterApplied = (
	filter: DashboardFilterInstance,
	payload?: FilterCombinationPayload | null
) => {
	if (!payload?.filters?.length) return false;

	const flat = flattenQueryFilters(payload);

	return flat.some((applied) => {
		return (
			applied.id === filter.filterId &&
			applied.operator === filter.operator &&
			JSON.stringify(applied.value) === JSON.stringify(filter.value)
		);
	});
};

const normalize = (value: unknown) => {
	if (value == null) return undefined;

	if (Array.isArray(value)) {
		return value.map(String).sort();
	}

	return String(value);
};

// Checks if a filters form row is already applied in the current payload.
export const matchesFormRow = (row: RowFilter, applied?: FilterPayload[] | null): boolean => {
	if (!applied?.length) return false;

	const rowValue = normalize(row.value);

	return applied.some((filter) => {
		const filterValue = normalize(filter.value);

		return (
			filter.id === row.filter &&
			filter.operator === row.operator &&
			JSON.stringify(filterValue) === JSON.stringify(rowValue)
		);
	});
};
